import { Fragment, useEffect, useState } from 'react';
import { api } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { formatQuantity, isNegativeQuantity, parseSignedQuantity, quantityError } from '../lib/quantity.js';

/**
 * Write stock off, or write it on, for a stated reason.
 *
 * THE REASON IS A FIXED SET AND A SELECT, NEVER FREE TEXT. "Damaged", "broke
 * it" and "dropped by driver" are one fact in four spellings, and a shrinkage
 * report over that column answers nothing. A reason that cannot be reported on
 * is a reason nobody uses.
 *
 * `private_use` IS DELIBERATELY ABSENT and the API refuses it by name. Stock
 * taken for the owner's private use is believed to be a deemed supply under
 * VAT Act s.18(1) — output VAT on the open market value, not on cost — which
 * is a different journal entirely and is not implemented. Recording it as an
 * ordinary write-off would post Dr 5150 / Cr 1450 at cost and declare no output
 * tax at all: a wrong VAT201, in the direction that under-declares, filed by
 * somebody who believed they had recorded the transaction properly. So the
 * option is not offered, and the note below says where it went instead.
 */
const REASONS = [
  ['damaged', 'Damaged'],
  ['stolen', 'Stolen'],
  ['obsolete', 'Obsolete'],
  ['expired', 'Expired'],
  ['count_correction', 'Count correction'],
  ['takeon_correction', 'Take-on correction'],
];

const REASON_LABEL = Object.fromEntries(REASONS);

const blankLine = () => ({
  key: crypto.randomUUID(), itemId: '', quantity: '', unitCost: '', serialNo: '', specs: [],
});

/**
 * How many items the picker asks for.
 *
 * The route defaults to 200 and this asks for the same number on purpose, so a
 * full list is a signal rather than a coincidence. A picker that silently omits
 * the item somebody is looking for is worse than a short table: it teaches them
 * the item does not exist.
 */
const ITEM_LIMIT = 200;

/**
 * Key/value pairs to the object the route takes, or null when nothing was said.
 *
 * Blank keys are dropped rather than sent as "": a half-typed row is not a
 * specification, and `{"": "i7"}` is a key nobody can search on later.
 */
function specsObject(pairs) {
  const out = {};
  for (const { k, v } of pairs ?? []) {
    if (k.trim()) out[k.trim()] = v.trim();
  }
  return Object.keys(out).length === 0 ? null : out;
}

/** True once anything has actually been said about the unit. */
const hasSpecs = (pairs) => (pairs ?? []).some((r) => r.k.trim());

const blankSpec = () => ({ id: crypto.randomUUID(), k: '', v: '' });

/**
 * What this particular machine IS.
 *
 * Free key/value pairs and not a fixed set of fields, because the column is
 * jsonb for a reason: a laptop, a lathe and a delivery van are not described by
 * the same words, and a fixed form would either ask everyone for a CPU or
 * quietly refuse to record a serial-numbered thing it had not anticipated.
 */
function SpecsEditor({ specs, onChange, allowed }) {
  const rows = specs ?? [];
  const set = (id, patch) => onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div style={{ padding: '2px 0 8px' }}>
      <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginBottom: 6 }}>
        {allowed ? (
          <>
            Specifications — what makes this unit this one. Optional, and the only place they can
            be recorded: the cost layer this line creates is what carries them.
          </>
        ) : (
          // VISIBLE WHILE NOT ALLOWED, and that is the whole point of this
          // branch. Hiding the editor the moment the line stopped qualifying
          // left the specs in state, posted them, and earned a refusal naming
          // a field that was no longer on screen — with no way to act on it
          // but to delete the line and start again.
          <>
            These specifications cannot go on this line any more. Remove them, or put the line
            back to a positive quantity of an individual item.
          </>
        )}
      </div>
      {rows.map((r) => (
        <div key={r.id} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <input value={r.k} placeholder="cpu" style={{ width: 160 }} disabled={!allowed}
                 onChange={(e) => set(r.id, { k: e.target.value })} />
          <input value={r.v} placeholder="i7" style={{ width: 220 }} disabled={!allowed}
                 onChange={(e) => set(r.id, { v: e.target.value })} />
          {/* Remove stays live even when the fields are locked: the user has to
              be able to do the thing the message asks of them. */}
          <button className="icon-btn" onClick={() => onChange(rows.filter((x) => x.id !== r.id))}>
            Remove
          </button>
        </div>
      ))}
      {allowed && (
        <button className="icon-btn" onClick={() => onChange([...rows, blankSpec()])}>
          {rows.length === 0 ? 'Describe this unit' : 'Add another'}
        </button>
      )}
      {!allowed && rows.length > 1 && (
        <button className="icon-btn" onClick={() => onChange([])}>Remove all</button>
      )}
    </div>
  );
}

export function StockAdjustments({ entity }) {
  const [adjustments, setAdjustments] = useState(null);
  const [items, setItems] = useState([]);
  // Whether the RESPONSE hit the cap — asked of the payload, before the
  // non_inventory filter below narrows it. Measuring the filtered list instead
  // meant a truncated 200-row response containing a single non-stocked item
  // came back at 199 and the warning never fired, which is precisely the silent
  // short list it exists to surface.
  const [itemsCapped, setItemsCapped] = useState(false);
  const [locations, setLocations] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = () => api.get(`/api/entities/${entity.id}/stock-adjustments`)
    .then((d) => setAdjustments(d.adjustments))
    .catch((e) => { setErr(e.message); setAdjustments([]); });

  useEffect(() => {
    if (!entity?.id) return;
    load();
    api.get(`/api/entities/${entity.id}/stock-items?active=true&limit=${ITEM_LIMIT}`)
      // A non_inventory item has no layers to adjust — it is not stocked at
      // all — so it is not offered rather than offered and refused.
      .then((d) => {
        setItemsCapped(d.items.length >= ITEM_LIMIT);
        setItems(d.items.filter((i) => i.kind !== 'non_inventory'));
      })
      .catch(() => {});
    api.get(`/api/entities/${entity.id}/stock-locations?active=true`)
      .then((d) => setLocations(d.locations)).catch(() => {});
    api.get(`/api/entities/${entity.id}/accounts`)
      .then((d) => setAccounts(d.accounts)).catch(() => {});
    /* eslint-disable-next-line */
  }, [entity?.id]);

  return (
    <>
      <ReportHead entity={entity} title="Stock adjustments"
                  sub="What happened to the stock, recorded with a reason that can be reported on." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {capturing ? (
        <AdjustmentForm entity={entity} items={items} itemsCapped={itemsCapped}
                        locations={locations} accounts={accounts}
                        onCancel={() => setCapturing(false)}
                        onPosted={(a) => {
                          setCapturing(false);
                          setMsg(`Adjustment ${a.number} posted${a.entryNo ? ` as journal ${a.entryNo}` : ', with no journal — it moved no cost'}.`);
                          load();
                        }} />
      ) : (
        <button className="btn" style={{ margin: '18px 0' }} onClick={() => setCapturing(true)}>
          Record an adjustment
        </button>
      )}

      {adjustments === null ? (
        <div className="empty">Loading…</div>
      ) : adjustments.length === 0 ? (
        <Empty>
          <p>No stock has been adjusted yet.</p>
          <p>Breakages, theft and stock written on are recorded here.</p>
        </Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Number</th>
                <th style={{ width: 110 }}>Date</th>
                <th style={{ width: 170 }}>Reason</th>
                <th>Journal</th>
                <th className="num" style={{ width: 130 }}>Cost moved</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {adjustments.map((a) => (
                <Fragment key={a.number}>
                  <tr>
                    <td className="code figure">{a.number}</td>
                    <td className="figure">{a.adjustmentDate}</td>
                    <td>{REASON_LABEL[a.reason] ?? a.reason}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                      {/* An adjustment that moved no cost posts no journal, and
                          that is legitimate rather than an error. */}
                      {a.entryNo ? `Journal ${a.entryNo}` : 'No journal — moved no cost'}
                    </td>
                    <td className="num"><Money value={a.total} /></td>
                    <td className="num">
                      <button className="icon-btn"
                              onClick={() => setExpanded(expanded === a.number ? null : a.number)}>
                        {expanded === a.number ? 'Close' : 'Lines'}
                      </button>
                    </td>
                  </tr>
                  {expanded === a.number && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--paper-sunk)' }}>
                        <table className="ledger">
                          <thead>
                            <tr>
                              <th style={{ width: 110 }}>Code</th>
                              <th>Item</th>
                              <th style={{ width: 160 }}>Serial</th>
                              <th style={{ width: 170 }}>Location</th>
                              <th className="num" style={{ width: 120 }}>Quantity</th>
                              <th className="num" style={{ width: 130 }}>Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {a.lines.map((l) => (
                              <tr key={l.movementId}>
                                <td className="code figure">{l.code}</td>
                                <td>{l.name}</td>
                                <td className="figure">{l.serialNo ?? '—'}</td>
                                <td>{l.locationCode}</td>
                                <td className="num figure">{formatQuantity(l.quantity)}</td>
                                <td className="num"><Money value={l.cost} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Capture one adjustment: one date, ONE LOCATION, one reason, many lines.
 *
 * The location is on the header because an adjustment is a physical event in a
 * physical place, and stock damaged in two store rooms is two events.
 *
 * A POSITIVE QUANTITY REQUIRES A UNIT COST AND A NEGATIVE ONE FORBIDS IT, and
 * the form enforces both. Stock written ON needs a cost because only the person
 * doing it knows what these units cost — a default would sit in inventory and
 * then in cost of sales with nothing behind it. Stock written OFF may not carry
 * one because FIFO already answers what leaves, layer by layer, and a second
 * opinion in the payload is a figure that can disagree with the layers.
 *
 * The API refuses both, and that refusal stays the backstop. It is not what the
 * user should meet first.
 */
function AdjustmentForm({ entity, items, itemsCapped, locations, accounts, onCancel, onPosted }) {
  const today = new Date().toISOString().slice(0, 10);
  const [head, setHead] = useState(() => ({
    adjustmentDate: today,
    locationId: locations.find((l) => l.isDefault)?.id ?? '',
    reason: 'damaged',
    contraAccountId: '',
  }));
  const [lines, setLines] = useState([blankLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Locations arrive after the first render, so the default is claimed once
  // they do rather than leaving the select blank.
  useEffect(() => {
    if (!head.locationId && locations.length) {
      setHead((h) => ({ ...h, locationId: locations.find((l) => l.isDefault)?.id ?? locations[0].id }));
    }
  }, [locations]); // eslint-disable-line

  const setLine = (key, patch) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const itemOf = (id) => items.find((i) => i.id === id);
  const contras = accounts.filter((a) => a.is_postable && a.is_active
    && ['operating_expense', 'cost_of_sales', 'other_income'].includes(a.subtype));

  /** What is wrong with this line, in the order a person would notice it. */
  const lineProblem = (line) => {
    if (!line.itemId) return 'Choose an item.';
    const bad = quantityError(line.quantity, { signed: true });
    if (bad) return bad;
    const qty = parseSignedQuantity(line.quantity);
    if (qty === '0.0000') return 'A quantity of nothing is not an adjustment.';
    const item = itemOf(line.itemId);
    if (item?.kind === 'individual' && !line.serialNo.trim()) {
      return 'An individual item is adjusted one serial at a time — name the unit.';
    }
    if (item?.kind === 'quantified' && line.serialNo.trim()) {
      return 'A quantified item has no serial number — if its units are distinguishable it is an individual item.';
    }
    // The same shape as the unit-cost rules below, and for the same reason:
    // adjustStock refuses both, and the refusal should reach the user while
    // the field is still on screen rather than as a server message naming
    // something they can no longer see.
    if (hasSpecs(line.specs) && item?.kind !== 'individual') {
      return 'Only an individual item has a unit to describe — a quantified layer is a measured '
        + 'amount, not a machine. Remove the specifications.';
    }
    if (hasSpecs(line.specs) && isNegativeQuantity(qty)) {
      return 'Stock written off was described when it was written on, and the layer it leaves from '
        + 'already carries that description. Remove the specifications.';
    }
    if (!isNegativeQuantity(qty)) {
      if (!String(line.unitCost).trim()) {
        return 'Stock written on needs a unit cost — only you know what these units cost, and a '
          + 'guessed figure would sit in inventory and then in cost of sales with nothing behind it.';
      }
    } else if (String(line.unitCost).trim()) {
      return 'Stock written off carries no cost of yours — FIFO decides what leaves, out of the '
        + 'layers these units actually came off. Clear the unit cost.';
    }
    return null;
  };

  const problems = lines.map(lineProblem);
  const ready = head.locationId && head.reason && problems.every((p) => p === null);

  const post = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        adjustmentDate: head.adjustmentDate,
        locationId: head.locationId,
        reason: head.reason,
        contraAccountId: head.contraAccountId || null,
        lines: lines.map((l) => {
          const qty = parseSignedQuantity(l.quantity);
          const item = itemOf(l.itemId);
          const out = { itemId: l.itemId, quantity: qty };
          if (l.serialNo.trim()) out.serialNo = l.serialNo.trim();
          // GATED BY KIND AND SIGN, not merely by what is in state. The editor
          // is one gate and lineProblem is another, but the send is the one
          // that decides what leaves the browser, and a field the user can no
          // longer see must never be one of them. ABSENT when nothing was said,
          // never `{}` — an empty object is a description and no description is
          // a different fact, which is why the column is nullable.
          const specs = item?.kind === 'individual' && !isNegativeQuantity(qty)
            ? specsObject(l.specs) : null;
          if (specs) out.specs = specs;
          // ABSENT, not null and not zero: the library has a different answer
          // for each, and a write-off must carry no cost at all.
          if (!isNegativeQuantity(qty)) out.unitCost = String(l.unitCost).trim();
          return out;
        }),
      };
      const { adjustment } = await api.post(`/api/entities/${entity.id}/stock-adjustments`, body);
      onPosted(adjustment);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ margin: '18px 0' }}>
      {err && <div className="error">{err}</div>}

      {itemsCapped && (
        <div className="note note--warn">
          <strong>The item list below stops at {ITEM_LIMIT}.</strong> If what you are adjusting is
          not in it, it exists — it is simply past the end of this list. Record the adjustment
          from a narrower item list rather than assuming the item is missing.
        </div>
      )}

      <div className="form-grid">
        <label className="field"><span>Date</span>
          <input type="date" value={head.adjustmentDate}
                 onChange={(e) => setHead({ ...head, adjustmentDate: e.target.value })} /></label>
        <label className="field" style={{ minWidth: 220 }}><span>Location</span>
          <select value={head.locationId} onChange={(e) => setHead({ ...head, locationId: e.target.value })}>
            <option value="">Choose a location</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
          </select>
        </label>
        <label className="field" style={{ minWidth: 200 }}><span>Reason</span>
          <select value={head.reason} onChange={(e) => setHead({ ...head, reason: e.target.value })}>
            {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="field" style={{ minWidth: 240 }}><span>Contra account</span>
          <select value={head.contraAccountId}
                  onChange={(e) => setHead({ ...head, contraAccountId: e.target.value })}>
            <option value="">The default for this reason</option>
            {contras.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
          </select>
        </label>
      </div>

      <div className="note">
        Stock taken for the owner's <strong>private use</strong> is not on this list on purpose. It
        is believed to be a deemed supply under VAT Act s.18(1), attracting output VAT on the open
        market value rather than on cost — a different journal, which this module does not compute.
        Recording it as damaged instead would understate the VAT201. Post it as a manual journal:
        debit drawings, credit inventory at cost, and declare the output tax on the deemed value.
      </div>

      <div className="scroll-x">
        <table className="ledger">
          <thead>
            <tr>
              <th style={{ minWidth: 240 }}>Item</th>
              <th style={{ width: 150 }}>Serial</th>
              <th className="num" style={{ width: 140 }}>Quantity</th>
              <th className="num" style={{ width: 140 }}>Unit cost</th>
              <th style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => {
              const item = itemOf(line.itemId);
              const qtyText = String(line.quantity).trim();
              const writingOff = qtyText !== '' && !quantityError(qtyText, { signed: true })
                && isNegativeQuantity(parseSignedQuantity(qtyText));
              return (
                <Fragment key={line.key}>
                  <tr>
                    <td>
                      <select value={line.itemId} style={{ width: '100%' }}
                              onChange={(e) => setLine(line.key, {
                                // SPECS GO WITH THE SERIAL. They describe a
                                // physical unit of THAT item; carrying them
                                // across an item change posts a description of
                                // one thing against another, and against a
                                // quantified item it is accepted and written
                                // onto a layer that has no unit to describe.
                                itemId: e.target.value, serialNo: '', specs: [],
                              })}>
                        <option value="">Choose an item</option>
                        {items.map((it) => (
                          <option key={it.id} value={it.id}>{it.code} — {it.name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input className="input--figure" value={line.serialNo} style={{ width: '100%' }}
                             disabled={item?.kind !== 'individual'}
                             placeholder={item?.kind === 'individual' ? 'SN-0001' : '—'}
                             onChange={(e) => setLine(line.key, { serialNo: e.target.value })} />
                    </td>
                    <td>
                      <input className="input--figure" value={line.quantity} style={{ width: '100%', textAlign: 'right' }}
                             placeholder="-3"
                             onChange={(e) => setLine(line.key, { quantity: e.target.value })} />
                    </td>
                    <td>
                      {/* DISABLED ONLY WHILE THERE IS NOTHING TO CLEAR. A flat
                          `disabled={writingOff}` had the same shape as the
                          specs bug above, one step milder: type a cost, correct
                          the quantity to -1, and lineProblem says "clear the
                          unit cost" over a field that cannot be cleared. The
                          user's only escape was deleting the line. Entry is
                          still refused on a write-off; correction is not. */}
                      <input className="input--figure" value={line.unitCost} style={{ width: '100%', textAlign: 'right' }}
                             disabled={writingOff && !String(line.unitCost).trim()}
                             placeholder={writingOff ? 'FIFO decides' : '0.00'}
                             onChange={(e) => setLine(line.key, { unitCost: e.target.value })} />
                    </td>
                    <td className="num">
                      {lines.length > 1 && (
                        <button className="icon-btn"
                                onClick={() => setLines((ls) => ls.filter((l) => l.key !== line.key))}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* SPECS, FOR THE ONE CASE WHERE THEY CAN BE CAPTURED AT ALL.
                      The route accepts them per line, and a machine written ON
                      creates the layer that will carry them — so this is the
                      only moment anyone can say what this particular unit is.
                      A write-off consumes a layer that was described when it
                      was created, and a quantified layer has no unit to
                      describe.
                      RENDERED WHENEVER THERE IS SOMETHING TO ACT ON, not only
                      when the line qualifies — the same call the unit-cost
                      input makes below. Specs already typed stay on screen
                      after the line stops qualifying, so the refusal names a
                      field the user can still reach. */}
                  {(item?.kind === 'individual' || hasSpecs(line.specs)) && (
                    <tr>
                      <td colSpan={5} style={{ paddingTop: 0 }}>
                        <SpecsEditor specs={line.specs}
                                     allowed={item?.kind === 'individual' && !writingOff}
                                     onChange={(specs) => setLine(line.key, { specs })} />
                      </td>
                    </tr>
                  )}
                  {problems[i] && (line.itemId || qtyText) && (
                    <tr>
                      <td colSpan={5} style={{ paddingTop: 0, fontSize: 12, color: 'var(--unbalanced)' }}>
                        {problems[i]}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button className="btn btn--quiet" onClick={() => setLines((ls) => [...ls, blankLine()])}>
          Add a line
        </button>
        <button className="btn" onClick={post} disabled={busy || !ready}>
          {busy ? 'Posting' : 'Post adjustment'}
        </button>
        <button className="btn btn--quiet" onClick={onCancel}>Cancel</button>
      </div>

      <div className="note">
        A negative quantity writes stock off and a positive one writes it on. Every adjustment
        posts a journal naming you, against its own account — kept out of cost of sales precisely
        so "our margin fell" and "we are being stolen from" stay legible as different
        conversations. A wrong one is reversed like any other entry; nothing here is unpickable.
        An adjustment that moved no cost at all posts no journal, which is legitimate rather than
        a failure.
      </div>
    </div>
  );
}
